// Utility functions for handling direct uploads to Google Cloud Storage using signed URLs

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Get signed URLs for uploading files directly to Cloud Storage
 * @param {Array} files - Array of file objects with properties: file, fieldName
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Response with signed URLs
 */
export const getSignedUploadUrls = async (files, token) => {
  const fileRequests = files.map(({ file, fieldName }) => ({
    originalName: file.name,
    contentType: file.type,
    size: file.size,
    fieldName
  }));

  const response = await fetch(`${API_URL}/upload/signed-urls`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ files: fileRequests })
  });

  if (!response.ok) {
    const error = await response.json();
    
    // Check if this is a service account issue
    if (response.status === 503 && error.fallbackToRegularUpload) {
      throw new Error('SIGNED_URLS_UNAVAILABLE: ' + error.message);
    }
    
    throw new Error(error.message || 'Failed to get upload URLs');
  }

  return response.json();
};

/**
 * Upload a file directly to Cloud Storage using a signed URL
 * @param {File} file - The file to upload
 * @param {string} signedUrl - The signed URL for upload
 * @param {string} contentType - The MIME type of the file
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Response>} - The upload response
 */
export const uploadFileToSignedUrl = async (file, signedUrl, contentType, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}. Response: ${xhr.responseText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    
    // Important: Set the ACL to make the file public
    xhr.setRequestHeader('x-goog-acl', 'public-read');
    
    xhr.send(file);
  });
};

/**
 * Upload multiple files in parallel with progress tracking
 * @param {Array} uploads - Array of upload objects with file, signedUrl, contentType
 * @param {Function} onProgress - Progress callback that receives (fileIndex, progress)
 * @returns {Promise<Array>} - Array of upload results
 */
export const uploadMultipleFiles = async (uploads, onProgress) => {
  const uploadPromises = uploads.map((upload, index) => {
    const { file, signedUrl, contentType } = upload;
    
    return uploadFileToSignedUrl(
      file, 
      signedUrl, 
      contentType,
      (progress) => onProgress && onProgress(index, progress)
    ).then(() => ({ success: true, index }))
     .catch((error) => ({ success: false, index, error }));
  });

  return Promise.all(uploadPromises);
};

/**
 * Verify that uploaded files are accessible in Cloud Storage
 * @param {Array} files - Array of file verification objects
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Verification response
 */
export const verifyUploads = async (files, token) => {
  const response = await fetch(`${API_URL}/upload/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ files })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to verify uploads');
  }

  return response.json();
};

/**
 * Clean up failed uploads from Cloud Storage
 * @param {Array} files - Array of file objects to clean up
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Cleanup response
 */
export const cleanupFailedUploads = async (files, token) => {
  const response = await fetch(`${API_URL}/upload/cleanup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ files })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to cleanup uploads');
  }

  return response.json();
};

/**
 * Complete workflow for uploading files directly to Cloud Storage
 * @param {Array} files - Array of file objects with properties: file, fieldName
 * @param {string} token - Authentication token
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Array>} - Array of successfully uploaded file URLs
 */
export const directUploadFiles = async (files, token, onProgress) => {
  try {
    // Step 1: Get signed URLs
    const signedUrlResponse = await getSignedUploadUrls(files, token);
    
    if (!signedUrlResponse.success) {
      throw new Error('Failed to get signed URLs');
    }

    // Step 2: Upload files directly to Cloud Storage
    const uploads = signedUrlResponse.files.map((signedFile) => {
      const originalFile = files.find(f => f.file.name === signedFile.originalName);
      return {
        file: originalFile.file,
        signedUrl: signedFile.signedUrl,
        contentType: originalFile.file.type,
        fileName: signedFile.fileName,
        publicUrl: signedFile.publicUrl,
        fieldName: signedFile.fieldName
      };
    });

    const uploadResults = await uploadMultipleFiles(uploads, onProgress);
    
    // Check for failed uploads
    const failedUploads = uploadResults.filter(result => !result.success);
    if (failedUploads.length > 0) {
      
      // Cleanup any failed uploads
      const cleanupFiles = failedUploads.map(failed => ({
        fileName: uploads[failed.index].fileName
      }));
      
      await cleanupFailedUploads(cleanupFiles, token);
      
      throw new Error(`${failedUploads.length} file(s) failed to upload`);
    }

    // Step 3: Verify uploads
    const verificationFiles = uploads.map(upload => ({
      fileName: upload.fileName,
      originalName: upload.file.name,
      fieldName: upload.fieldName
    }));

    const verificationResult = await verifyUploads(verificationFiles, token);
    
    if (!verificationResult.success) {
      throw new Error('Upload verification failed');
    }

    // Return the public URLs organized by field name
    const organizedResults = {};
    verificationResult.files.forEach(file => {
      if (file.success) {
        if (!organizedResults[file.fieldName]) {
          organizedResults[file.fieldName] = [];
        }
        organizedResults[file.fieldName].push(file.publicUrl);
      }
    });

    return organizedResults;

  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to validate file before upload
 * @param {File} file - The file to validate
 * @param {string} fieldName - The field name (audioFile, audioFiles, coverArt)
 * @returns {Object} - Validation result
 */
export const validateFileForUpload = (file, fieldName) => {
  const errors = [];
  
  // Size validation (200MB limit)
  if (file.size > 200 * 1024 * 1024) {
    errors.push('File size must be less than 200MB');
  }
  
  // Type validation
  if (fieldName === 'coverArt') {
    if (!file.type.startsWith('image/')) {
      errors.push('Cover art must be an image file');
    } else if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      errors.push('Cover art must be in JPEG, PNG, or WebP format');
    }
  } else if (fieldName === 'audioFiles' || fieldName === 'audioFile') {
    if (!file.type.startsWith('audio/')) {
      errors.push('Audio files must have audio MIME type');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
